import { z } from 'zod';
import { BaseSchema } from '../base/base.entity.model';
import * as FlatModels from '../../internal';
import { omitBy } from 'lodash';

export const NodeSettingsSchema = z.object({
  hideHomeButton: z.boolean().nullable().optional(),
  hideDownloadPdf: z.boolean().nullable().optional(),
  hideBreadcrumbLink: z.boolean().nullable().optional(),
  hideAnnotations: z.boolean().nullable().optional(),
  pageTitleColor: z.string().max(50).nullable().optional(),
  emails: z.string().max(255)
            .optional()
            .transform((val) => (val === '' ? null : val))
            .nullable()
            .refine((value) => {
              if (!value?.length) return true; // Allow nullable and optional
              const emailsOk = z.array(z.string().trim().email()).safeParse(value.split('\n'));
              return emailsOk.success;
            }),
  quizNavSelection: z.boolean().nullable().optional(),  // used to hide all other nav-points on the same level which are not QuizStart
});

export type NodeSettings = z.infer<typeof NodeSettingsSchema>;

export const nodeSettingDefaults: NodeSettings = {
  hideHomeButton: false,
  hideDownloadPdf: false,
  hideBreadcrumbLink: false,
  hideAnnotations: false,
  emails: '',
  pageTitleColor: null,
  quizNavSelection: false,
};

export type nodeSettingType = {
  prop: keyof NodeSettings;
  label: string;
  placeholder?: string;
  type: 'boolean' | 'color' | 'textarea' | 'string';
  onlyUsedBy?: string[]; // currently only used for 'quiz'
  noInheritance?: boolean; // setting will appear on top of inheritable settings
};

export const SettingsProps: nodeSettingType[] = [
  { prop: 'hideHomeButton', label: 'global.hide_home_button', type: 'boolean' },
  { prop: 'hideDownloadPdf', label: 'global.hide_pdf_download', type: 'boolean' },
  { prop: 'hideAnnotations', label: 'global.hide_annotations', type: 'boolean' },
  { prop: 'hideBreadcrumbLink', label: 'global.hide_breadcrumb_link', type: 'boolean' },
  {
    prop: 'emails',
    label: 'global.support_emails',
    placeholder: 'global.email_per_line',
    type: 'textarea',
  },
  {
    prop: 'quizNavSelection',
    label: 'global.hide_quiz_navigation',
    placeholder: 'global.hide_quiz_navigation_instruction',
    type: 'boolean',
    onlyUsedBy: ['quiz'],
    noInheritance: true,  // will appear on top of settings
  },
] as const;

// Settings that are only visible to certain user roles (admin, author, editor)
export const ProtectedSettings: Partial<Record<typeof SettingsProps[number]['prop'], boolean>> = {
  emails: true
}

export const NodeSchema = BaseSchema.extend({
  parentId: z.string().max(255).nullable().default(null),
  parent: z.string().uuid().nullable(),
  position: z.number(),
  page: z.string().uuid(),
  children: z.array(z.string().uuid()).default([]),
  hasChildren: z.boolean().optional(),
  route: z.string().regex(/^[a-zA-Z0-9-_.+~]+$/).max(510).nullable().default(null),
  hidden: z.boolean().default(false),
  restricted: z.boolean().default(false),
  shopId: z.string().max(6).nullable().default(null),
  shares: z.array(z.string().uuid()).default([]),
  settings: NodeSettingsSchema.nullable(),
  expanded: z.boolean().optional(),
});

export type Node = z.infer<typeof NodeSchema>;

export const ElearningNodeSchema = NodeSchema;
/*
export const ElearningNodeSchema = NodeSchema.extend({
  type: z.literal('elearning').default('elearning'),
});
*/

export type ElearningNode = z.infer<typeof ElearningNodeSchema>;

export const SubjectNodeSchema = NodeSchema;
/*
export const SubjectNodeSchema = NodeSchema.extend({
  type: z.literal('subject').default('subject'),
});
*/

export type SubjectNode = z.infer<typeof SubjectNodeSchema>;

export const ChapterNodeSchema = NodeSchema;
/*
export const ChapterNodeSchema = NodeSchema.extend({
  type: z.literal('chapter').default('chapter'),
});
*/

export type ChapterNode = z.infer<typeof ChapterNodeSchema>;

export const BlockNodeSchema = NodeSchema;
/*
export const BlockNodeSchema = NodeSchema.extend({
  type: z.literal('block').default('block'),
});
*/

export type BlockNode = z.infer<typeof BlockNodeSchema>;

export const SlideNodeSchema = NodeSchema;
/*
export const SlideNodeSchema = NodeSchema.extend({
  type: z.literal('slide').default('slide'),
});
*/

export type SlideNode = z.infer<typeof SlideNodeSchema>;

export type settingsType = {
  settings: NodeSettings;
  inheritedSettings: NodeSettings | null;
  node?: NodeEntityType;
}

export function determineSettings(
  node: RouteElement | Node,
  parentNodes: RouteElement[] | Node[],
) {
  let inherited: NodeSettings = {}
  const current: NodeSettings = omitBy(node.settings ?? {}, (value) => value === null || value === undefined);

  const parentNodesSorted = node.parentId && parentNodes[0]?.id === node.parentId ? [...parentNodes].reverse() : parentNodes;
  for (const parentNode of parentNodesSorted) {
    const cleanSettings: NodeSettings = omitBy(parentNode.settings ?? {}, (value) => value === null || value === undefined);
    inherited = Object.assign(inherited, cleanSettings);
  }
  
  const combined = Object.assign({}, inherited, current);

  const combinedWithDefaults = Object.assign({}, nodeSettingDefaults, combined)
  const inheritedWithDefaults = Object.assign({}, nodeSettingDefaults, inherited)
  const currentWithDefaults = Object.assign({}, nodeSettingDefaults, current)

  return {
    current,
    inherited,
    combined,
    currentWithDefaults,
    inheritedWithDefaults,
    combinedWithDefaults
  }
}

export const NodeEntityTypeSchema = z.union([
  ElearningNodeSchema,
  SubjectNodeSchema,
  ChapterNodeSchema,
  BlockNodeSchema,
  SlideNodeSchema,
]);

export type NodeEntityType = z.infer<typeof NodeEntityTypeSchema>;

export const NodeMapSchema = z.object({
  entities: z.array(NodeEntityTypeSchema).default([]),
  map: z.record(z.string(), z.number()),
  parentNodeCount: z.number().default(0),
});

export type NodeMap = z.infer<typeof NodeMapSchema>;

export const NodeEntityQueryResultSchema = FlatModels.EntityControlModels.EntityQueryResultSchema.extend({
  entity: NodeEntityTypeSchema,
});

export type NodeEntityQueryResult = z.infer<typeof NodeEntityQueryResultSchema>;

export const NodeRouteSchema = z.object({
  route: z.string().regex(/([a-zA-Z0-9-_.+~]*\/)*([a-zA-Z0-9-_.+~]*)?/)
});

export const RouteElementSchema = z.object({
  id: z.string().uuid(),
  route: z.string(),
  path: z.string(),
  depth: z.number(),
  sort: z.string(),
  hasChildren: z.number(),
  parentId: z.string().uuid().nullable(),
  settings: z.any().nullable(),
});

export type RouteElement = z.infer<typeof RouteElementSchema>;

export const ReservedRouteNames = new Set<string>([
  '404',
  '403',
  'login',
  'forgot_password',
  'reset_password',
  'profile',
  'changelog',
  'users',
  'user',
  'media',
  'matomo-stats',
  'detached',
  'import-stats',
  'metadata',
  'copyrights',
  'copyright',
  'modalities',
  'modality',
  'stains',
  'stain',
  'search',
  'node',
  'slide',
  // Viewer
  'folie',
  'seite',
  'quiz'
]);
